import React, {ReactElement} from 'react';
import {Helmet} from 'react-helmet';
import {useStaticQuery, graphql} from 'gatsby';
import {useIntl} from 'react-intl';
import {LANGUAGE_LOCALE} from '@fo/shared-types/constants';

interface Props {
  description?: string;
  meta?: {
    name: string;
    content: string;
  }[];
  title?: string;
  titleTemplate?: string;
  link?: {
    rel: string;
    href: string;
  }[];
  scripts?: {
    type: string | null;
    innerHTML: string | null;
  }[];
  slug?: string;
}

function SEO({
  description = '',
  meta = [],
  title,
  link = [],
  scripts = [],
  titleTemplate,
}: Readonly<Props>): ReactElement {
  const {formatMessage} = useIntl();
  const {site} = useStaticQuery(
    graphql`
      query SEO {
        site {
          siteMetadata {
            title
            description
            author
          }
        }
      }
    `
  );

  const metaDescription = description || site.siteMetadata.description;

  const titleSuffix = ` | ${site.siteMetadata.title}`;
  const maxCharacters = 60 - titleSuffix.length;
  const titleTemplateData = title && title.length > maxCharacters ? titleTemplate : `%s${titleSuffix}`;

  return (
    <Helmet
      bodyAttributes={{class: 'notranslate'}}
      defaultTitle={formatMessage({id: 'seo.defaultMetaTitle'})}
      htmlAttributes={{
        lang: LANGUAGE_LOCALE,
      }}
      title={title}
      titleTemplate={titleTemplateData}
      meta={[
        {
          name: 'description',
          content: metaDescription,
        },
        {
          property: 'og:title',
          content: title,
        },
        {
          property: 'og:description',
          content: metaDescription,
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          name: 'twitter:card',
          content: 'summary',
        },
        {
          name: 'twitter:creator',
          content: site.siteMetadata.author,
        },
        {
          name: 'twitter:title',
          content: title,
        },
        {
          name: 'twitter:description',
          content: metaDescription,
        },
      ].concat(meta)}
      link={link}
      script={[...scripts]}
    />
  );
}

export default SEO;
